import { ClientConfig, createClient, SanityClient } from '@sanity/client';
import groq from 'groq';

import { getClientValidatedEnv } from '../../config/client-env';
import { SanityClientFetchOptions } from '../../data/sanity/types';
import { isSSR } from '../../utils/helpers';
import { logs } from '../../utils/logs';
import { MetricsService } from '../MetricsService/MetricsService';

class SanityFetcherServiceImplementation {
  private client?: SanityClient;
  private queue: Array<{
    query: string;
    options: SanityClientFetchOptions;
    resolve: (value: any) => void;
    reject: (reason?: any) => void;
  }> = [];
  private isProcessingQueue = false;
  private queueHold = false;

  // throttle-related
  private throttleWarningSent = false;
  private lastRequestTime: number = Date.now();
  private requestsTotal = 0;
  private requestCount = 0;
  private requestLimitPerInterval = 100;
  // 1 minute
  private limitTimeIntervalMs = 60 * 1000;
  // 2 seconds
  private throttleInitialDelayMs = 2 * 1000;
  // 15 seconds
  private throttleMaxDelayMs = 15 * 1000;

  getProjectSettings(): Partial<ClientConfig> {
    return {
      projectId:
        getClientValidatedEnv().NEXT_PUBLIC_SANITY_WORKSPACE ??
        getClientValidatedEnv().SANITY_STUDIO_WORKSPACE,
      dataset:
        getClientValidatedEnv().NEXT_PUBLIC_SANITY_DATASET ??
        getClientValidatedEnv().SANITY_STUDIO_DATASET,
      token: getClientValidatedEnv().SANITY_STUDIO_MANAGEMENT_TOKEN,
    };
  }

  async _waitClientSideThrottleOk(
    query: string,
    options: SanityClientFetchOptions
  ) {
    const currentTime = Date.now();
    if (currentTime - this.lastRequestTime > this.limitTimeIntervalMs) {
      this.requestCount = 0;
      this.lastRequestTime = currentTime;
    }

    this.requestCount++;

    if (this.requestCount > this.requestLimitPerInterval) {
      const message = `High number of Sanity GROQ requests detected, throttling requests to avoid quota overage`;

      const excessAmount = this.requestCount - this.requestLimitPerInterval;
      const overPercentage = excessAmount / this.requestLimitPerInterval;
      const waitTime = Math.min(
        this.throttleInitialDelayMs * overPercentage,
        this.throttleMaxDelayMs
      );

      const extra = {
        query,
        options: JSON.stringify(options, undefined, 2),
        href: !isSSR() ? window.location.href : '[Server-side]',
        excessAmount,
        waitTime,
      };

      // useful while developing locally
      logs.error(
        {
          message,
          clientSideThrottle: {
            ...extra,
          },
        },
        'SANITY_CLIENT_SIDE_THROTTLE'
      );

      if (!this.throttleWarningSent) {
        this.throttleWarningSent = true;

        const Sentry = await import('@sentry/nextjs');

        if (!isSSR()) {
          Sentry.captureException(new Error(message), {
            extra,
          });
        }
      }

      await new Promise((resolve) => setTimeout(resolve, waitTime));
    }
  }

  async fetchAndLog<T>(
    query: string,
    options: SanityClientFetchOptions = {}
  ): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      this.queue.push({ query, options, resolve, reject });
      this.processQueue();
    });
  }

  /**
   * Captures all incoming Sanity requests during the callback execution, and runs all requests in one batch once callback has finished executing.
   * NOTE! This currently has a side-effect of pausing and batching all other requests made outside of the provided callback
   * @param callback - Contains function calls that make Sanity requests, all to be executed in one batch.
   * @returns Promise that resolves to an array of results from the provided callback.
   */
  async batchRequestsFromCallback<T extends Array<unknown>>(
    callback: () => T
  ): Promise<{ [K in keyof T]: Awaited<T[K]> }> {
    this.holdQueue();
    const result = callback();
    this.unholdQueue();
    return Promise.all(result) as Promise<{ [K in keyof T]: Awaited<T[K]> }>;
  }

  private async processQueue() {
    if (this.isProcessingQueue || this.queueHold || this.queue.length === 0) {
      return;
    }

    this.isProcessingQueue = true;

    while (this.queue.length > 0 && !this.queueHold) {
      const queueItems = this.queue.splice(0, this.queue.length);
      const wasCombined = queueItems.length > 1;

      const firstItem = queueItems[0]!;

      const combinedQuery = wasCombined
        ? groq`[
          ${queueItems.map((item) => item.query).join(',\n')}
        ]`
        : firstItem.query;
      try {
        const client = this.getClient(firstItem.options);

        // don't throttle anything server-side
        if (!firstItem.options.noThrottle && !isSSR()) {
          await this._waitClientSideThrottleOk(
            combinedQuery,
            firstItem.options
          );
        }

        const { NEXT_PUBLIC_SANITY_DEBUG_QUERIES } = getClientValidatedEnv();
        if (NEXT_PUBLIC_SANITY_DEBUG_QUERIES) {
          logs.debug(
            {
              sanity: {
                query: combinedQuery,
                options: firstItem.options,
                wasCombined,
              },
            },
            'SANITY_QUERY_DEBUG'
          );
        }

        const queryExecutionTimeStart = Date.now();

        this.requestsTotal++;
        const sanityResult = await client.fetch(combinedQuery, undefined, {
          filterResponse: false,
        });

        MetricsService.histogramObserve({
          name: 'sanity_fetch',
          value: sanityResult.ms / 1000,
        });

        const result = sanityResult.result;

        if (NEXT_PUBLIC_SANITY_DEBUG_QUERIES) {
          const executionTime = Date.now() - queryExecutionTimeStart;
          logs.debug(
            {
              sanity: {
                requestCount: this.requestsTotal,
                reportedExecutionTime: sanityResult.ms,
                executionTime,
              },
            },
            'SANITY_QUERY_DEBUG'
          );
        }

        queueItems.forEach((item, index) =>
          item.resolve(wasCombined ? result[index] : result)
        );
      } catch (error) {
        queueItems.forEach((item) => item.reject(error));
      }
    }

    this.isProcessingQueue = false;
  }

  private holdQueue() {
    this.queueHold = true;
  }

  private unholdQueue() {
    this.queueHold = false;
    this.processQueue();
  }

  getClient(options: SanityClientFetchOptions) {
    if (!this.client) {
      this.client = createClient({
        ...this.getProjectSettings(),
        useCdn: true,
        apiVersion: '2023-10-02',
        perspective: options.useRawPerspective ? 'raw' : 'published',
      });
    }

    if (!options?.draftMode && options?.skipCdn) {
      return this.client.withConfig({
        useCdn: false,
        ignoreBrowserTokenWarning: true,
      });
    }

    if (options?.draftMode) {
      const previewToken =
        getClientValidatedEnv().NEXT_PUBLIC_SANITY_PREVIEW_TOKEN;

      if (previewToken) {
        return this.client.withConfig({
          token: previewToken,
          useCdn: false,
          ignoreBrowserTokenWarning: true,
          perspective: 'previewDrafts',
        });
      }
    }

    return this.client as SanityClient;
  }

  removeParamsFromUrl(imageUrl: string): string {
    return imageUrl.split('?')[0]!;
  }
}

export const SanityFetcherService = new SanityFetcherServiceImplementation();
